/**
 * Storage durability checks before offline enrollment (Phase 16A, design §3.1 / §3.2).
 * Android evicts "best-effort" storage under pressure, so enrollment there requires a granted
 * persist(). iOS keeps installed-app storage while the app is used; persist() is requested anyway.
 */
import type { DevicePlatform } from './deviceClass';

/** Below this much free space enrollment stops; below WARN it shows a warning. */
export const MIN_FREE_BYTES = 200 * 1024 * 1024;
export const WARN_FREE_BYTES = 50 * 1024 * 1024;

export async function requestPersistentStorage(): Promise<boolean> {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted?.()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

/** Free bytes the origin may still use, or null when the browser does not say. */
export async function freeStorageBytes(): Promise<number | null> {
  try {
    const estimate = await navigator.storage?.estimate?.();
    if (!estimate || typeof estimate.quota !== 'number') return null;
    return Math.max(0, estimate.quota - (estimate.usage || 0));
  } catch {
    return null;
  }
}

export interface ReadinessInput {
  deviceClass: 'MOBILE' | 'DESKTOP';
  platform: DevicePlatform;
  standalone: boolean;
  online: boolean;
  /** The agency's plan includes offline mode (Core and above). */
  planIncludesOffline: boolean;
  canStartVisits: boolean;
  cryptoAvailable: boolean;
  indexedDbAvailable: boolean;
}

export interface ReadinessProblem {
  id: 'plan' | 'device' | 'permission' | 'install' | 'online' | 'browser';
  message: string;
}

/** Problems that stop enrollment, in the order the clinician should fix them. Pure. */
export function enrollmentProblems(input: ReadinessInput): ReadinessProblem[] {
  const problems: ReadinessProblem[] = [];
  if (!input.planIncludesOffline) problems.push({ id: 'plan', message: 'Offline mode is included with the Core plan and above. Ask your agency administrator.' });
  if (input.deviceClass !== 'MOBILE') problems.push({ id: 'device', message: 'Offline mode works on phones and tablets only.' });
  if (!input.canStartVisits) problems.push({ id: 'permission', message: 'Offline mode is for clinicians who run visits.' });
  if (!input.cryptoAvailable || !input.indexedDbAvailable) problems.push({ id: 'browser', message: 'This browser can\'t store data securely. Use Safari on iPhone/iPad or Chrome on Android.' });
  if (input.deviceClass === 'MOBILE' && !input.standalone) problems.push({ id: 'install', message: 'Install pelora on this device first, then open it from the Home Screen.' });
  if (!input.online) problems.push({ id: 'online', message: 'Connect to the internet to set up offline mode.' });
  return problems;
}
